"use client";

import React, { useState, useRef } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { useSavedVacancies } from '@/context/SavedVacanciesContext';
import styles from './ApplyModal.module.css';
import { useAlert } from '@/context/AlertContext';
import { useLanguage } from '@/context/LanguageContext';

import { submitApplication } from '@/app/actions/applicationActions';

interface ApplyModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyName?: string;
  companyLogo?: string;
  jobId?: string | number;
  jobTitle?: string;
  applicationEmail?: string;
}

export default function ApplyModal({ isOpen, onClose, companyName = 'Company Name', companyLogo, jobId, jobTitle = 'Vacancy', applicationEmail }: ApplyModalProps) {
  const { data: session } = useSession();
  const { t } = useLanguage();
  const params = useParams();
  const lang = params?.lang || 'en';

  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const { markAsSent } = useSavedVacancies();
  const { showAlert } = useAlert();
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const sessionNameParts = session?.user?.name ? session.user.name.split(' ') : [];
  const defaultFirstName = sessionNameParts[0] || '';
  const defaultLastName = sessionNameParts.slice(1).join(' ') || '';
  const defaultEmail = session?.user?.email || '';
  const defaultMobile = (session?.user as any)?.phone || (session?.user as any)?.mobile || '';

  if (!isOpen) return null;

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/[^\d+]/g, '');
    
    // Automatically prepend +355 if they type a local number
    if (val && !val.startsWith('+')) {
      if (val.startsWith('0')) val = val.substring(1);
      val = '+355' + val;
    }
    
    // Remove +355 for formatting logic
    if (val.startsWith('+355')) {
      let nums = val.replace('+355', '').replace(/\D/g, '');
      let formatted = '+355';
      if (nums.length > 0) formatted += ' ' + nums.substring(0, 2);
      if (nums.length > 2) formatted += ' ' + nums.substring(2, 5);
      if (nums.length > 5) formatted += ' ' + nums.substring(5, 8);
      e.target.value = formatted;
    } else {
      e.target.value = val;
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      if (selectedFile.size > 4 * 1024 * 1024) {
        setErrorMsg('File size exceeds the 4MB limit. Please choose a smaller file.');
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } else {
        setErrorMsg('');
        setFile(selectedFile);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg('');
    
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    // Add extra needed details for the email and backend
    formData.append('vacancy_id', String(jobId || ''));
    if (applicationEmail) formData.append('company_email', applicationEmail);
    formData.append('company_name', companyName);
    formData.append('job_title', jobTitle);
    formData.append('lang', String(lang || 'sq'));
    
    if (file) {
      formData.append('resume', file);
    } else {
      setErrorMsg('Please upload your resume.');
      setIsSubmitting(false);
      return;
    }

    const phoneStr = formData.get('applicant_phone') as string || '';
    if (phoneStr.replace(/\D/g, '').length < 11) {
      setErrorMsg(lang === 'sq' ? 'Ju lutem shkruani një numër telefoni të vlefshëm.' : 'Please enter a valid phone number.');
      setIsSubmitting(false);
      return;
    }
    
    let result;
    try {
      console.log("Starting submission to Server Action...");
      // debugger; // Pause here so you can step through
      
      const submitPromise = submitApplication(formData);
      const timeoutPromise = new Promise<any>((_, reject) => 
        setTimeout(() => reject(new Error('The request timed out after 45 seconds. The server might be hanging.')), 45000)
      );
      
      result = await Promise.race([submitPromise, timeoutPromise]);
      console.log("Server action returned:", result);
    } catch (err: any) {
      console.error("Caught error in submit:", err);
      showAlert("System caught an error: " + (err.message || String(err)), 'Error', 'error');
      result = { success: false, error: err.message || 'Network error or file too large' };
    }

    setIsSubmitting(false);
    
    if (result.success) {
      if (jobId) {
        markAsSent(jobId);
      }
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        onClose();
      }, 2500);
    } else {
      const errMsg = result.error || 'Failed to send application. Please try again.';
      setErrorMsg(errMsg);
      showAlert("Submission failed: " + errMsg, 'Error', 'error');
    }
  };

  return (
    <div className={styles.overlay} onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t('Send Resume')}</h2>
          <p className={styles.subtitle}>{t('Fill Information')}</p>
          <button className={styles.closeButton} onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        {isSuccess ? (
          <div className={styles.successMessage}>
            <div className={styles.successIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"></polyline></svg>
            </div>
            <h3>{t('Application Sent Successfully!')}</h3>
            <p style={{ color: '#6b7280' }}>{t('Your resume has been forwarded to the company.')}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.body}>
              <div className={styles.row}>
                <div className={styles.inputWrapper}>
                  <input name="applicant_first" type="text" placeholder={t('Name')} className={styles.input} defaultValue={defaultFirstName} required />
                  <span className={styles.requiredAsterisk}>*</span>
                </div>
                <div className={styles.inputWrapper}>
                  <input name="applicant_last" type="text" placeholder={t('Surname')} className={styles.input} defaultValue={defaultLastName} required />
                  <span className={styles.requiredAsterisk}>*</span>
                </div>
              </div>

              <div className={styles.inputWrapper}>
                <input name="applicant_email" type="email" placeholder={t('Email Address')} className={styles.input} defaultValue={defaultEmail} required />
                <span className={styles.requiredAsterisk}>*</span>
              </div>
              
              <div className={styles.inputWrapper}>
                <input 
                  name="applicant_phone" 
                  type="tel" 
                  placeholder={t('Mobile (+355 xx xxx xxx)')} 
                  className={styles.input} 
                  defaultValue={(() => {
                    let val = defaultMobile;
                    if (val.startsWith('0')) val = val.substring(1);
                    if (!val.startsWith('+')) val = '+355' + val;
                    if (val.startsWith('+355')) {
                      let nums = val.replace('+355', '').replace(/\D/g, '');
                      let formatted = '+355';
                      if (nums.length > 0) formatted += ' ' + nums.substring(0, 2);
                      if (nums.length > 2) formatted += ' ' + nums.substring(2, 5);
                      if (nums.length > 5) formatted += ' ' + nums.substring(5, 8);
                      return formatted;
                    }
                    return val;
                  })()}
                  onChange={handlePhoneChange} 
                  maxLength={16}
                  required 
                />
                <span className={styles.requiredAsterisk}>*</span>
              </div>

              <div 
                className={styles.uploadBox} 
                onClick={() => fileInputRef.current?.click()}
              >
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  onChange={handleFileChange} 
                  accept=".pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  name="resume"
                />
                <div className={`${styles.uploadIcon} ${file ? styles.uploadIconActive : ''}`}>
                  {file ? (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                  )}
                </div>
                <div className={styles.uploadText} style={{ flexGrow: 1 }}>
                  <h4>{file ? file.name : t('Upload Resume *')}</h4>
                  <p>{file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : t('pdf, docx, doc • Max file size 4MB')}</p>
                </div>
                {file && (
                  <div style={{ display: 'flex', alignItems: 'center', paddingRight: '0.5rem' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                )}
              </div>

              <div className={styles.companyBox}>
                <div className={styles.companyIcon}>
                  {companyLogo ? (
                    <img src={companyLogo} alt={companyName} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '4px' }} />
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>
                  )}
                </div>
                <div className={styles.companyName}>{companyName}</div>
              </div>

              <div className={styles.checkboxRow}>
                <input type="checkbox" id="terms" className={styles.checkbox} required />
                <label htmlFor="terms" className={styles.checkboxLabel}>
                  {t('I agree to the')} <Link href={`/${lang}/terms-of-service`} target="_blank" className={styles.link}>{t('Terms & Conditions')}</Link>, {t('and accept the')} <Link href={`/${lang}/data-processing`} target="_blank" className={styles.link}>{t('processing of my personal data')}</Link>
                </label>
              </div>
            </div>

            {errorMsg && (
              <div style={{ color: 'red', fontSize: '14px', padding: '0 24px', marginBottom: '16px' }}>
                {errorMsg}
              </div>
            )}

            <div className={styles.footer}>
              <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
                {isSubmitting ? t('Sending...') : t('Send')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
